<script setup lang="ts">
defineProps<{
  isLoading: boolean;
  isLoadingMore: boolean;
  isOverlayActive: boolean;
  searchText: string;
  searchResults: Array<any>;
  searchCategories: Array<any>;
  searchPagination: any;
  sportsResults: Array<any>;
  isActiveCategory: string;
  isSportsTabActive: boolean;
  calculateWidthStyle: any;
}>();

const { isMobile, isTablet } = useLayout();

const spaceBetweenDesk = 18;
const spaceBetweenMobi = 12;
const spaceBetweenTabl = 16;
const perRowDesk = 6;
const perRowMobi = 4;
const perRowTabl = 4;

const spaceBetween = computed<number>(() => {
  if (isMobile.value) {
    return spaceBetweenMobi;
  } else if (isTablet.value) {
    return spaceBetweenTabl;
  } else {
    return spaceBetweenDesk;
  }
});

const perRow = computed<number>(() => {
  if (isMobile.value) {
    return perRowMobi;
  } else if (isTablet.value) {
    return perRowTabl;
  } else {
    return perRowDesk;
  }
});

const emit = defineEmits(['setActiveCategory', 'getMoreGames']);

const setActiveCategory = (val: string) => {
  emit('setActiveCategory', val);
};

const getMoreGames = (val: number) => {
  const scrollingDivElement = document.getElementsByClassName('resultsWrapper');

  if (scrollingDivElement && scrollingDivElement[0]) {
    const elementHeight = scrollingDivElement[0].scrollHeight;

    setTimeout(() => {
      if (elementHeight && scrollingDivElement[0]) {
        scrollingDivElement[0].scrollTop = elementHeight - 100;
      }
    }, 100);
  }

  emit('getMoreGames', val);
};
</script>

<template>
  <div
    v-if="isOverlayActive && searchText.length > 0"
    class="searchResults"
  >
    <div
      v-if="isSportsTabActive && !isLoading && searchCategories.length"
      class="filterWrapper"
    >
      <div
        v-for="cat in searchCategories"
        :key="cat.id"
        :class="['btn', { categoryActive: isActiveCategory === cat.slug }]"
        @click="setActiveCategory(cat.slug)"
      >
        <span>{{ cat.name }}</span>
      </div>
    </div>

    <div
      v-else-if="isLoading"
      class="filterWrapper sleletonSports"
    >
      <div
        v-for="(n, index) in isSportsTabActive ? 3 : 4"
        :key="`skeleton-${index}`"
        :class="[
          'skeleton-loading btn',
          isSportsTabActive ? 'sport-layout' : 'casino-layout',
        ]"
      ></div>
    </div>

    <template
      v-if="isSportsTabActive && !isLoading && sportsResults.length > 0"
    >
      <div class="resultsWrapper">
        <section class="sportsItems">
          <SportsCactusSportbookListItem
            v-for="event in sportsResults"
            :key="event.id"
            :category="isActiveCategory"
            :data="event"
          />
        </section>
      </div>
    </template>
    <template v-else-if="searchResults.length >= 1">
      <div class="filterWrapper">
        <button
          v-if="Object.keys(searchCategories).length > 0"
          :class="['btn', { categoryActive: isActiveCategory === 'all' }]"
          @click="setActiveCategory('all')"
        >
          <span>{{ $t('components.search_bar.all_categories') }}</span>
        </button>
        <button
          v-for="cat in searchCategories"
          :key="cat.id"
          :class="['btn', { categoryActive: isActiveCategory === cat.slug }]"
          @click="setActiveCategory(cat.slug)"
        >
          <span>{{ cat.name }}</span>
        </button>
      </div>
      <div class="resultsWrapper">
        <CasinoMediaList
          display-mode="list"
          :is-loading="isLoading"
          :is-loading-more-games="isLoadingMore"
          :items="searchResults"
          :per-row="[perRowDesk, perRowTabl, perRowMobi]"
          :space-between="[
            spaceBetweenDesk,
            spaceBetweenTabl,
            spaceBetweenMobi,
          ]"
          type="game"
        />
      </div>
      <CasinoGamesPagination
        v-if="Object.keys(searchPagination).length > 0"
        class="pt-5 mb-4"
        :pagination="searchPagination"
        @load-more-games="getMoreGames(searchPagination.nextPage)"
      />
    </template>
    <section
      v-else-if="
        (isSportsTabActive ? !sportsResults.length : !searchResults.length) &&
        !isLoading
      "
      class="p-4"
    >
      <div
        class="flex items-center justify-center self-center alert alert-warning"
      >
        <span class="">
          {{ $t('components.search_bar.no_results') }}
        </span>
      </div>
    </section>
    <section
      v-else-if="isLoading"
      class="p-4"
    >
      <div
        class="skeleton-rows"
        :class="isSportsTabActive ? 'sport-layout' : 'casino-layout'"
        :style="
          isSportsTabActive
            ? ''
            : `gap: ${spaceBetween}px;grid-template-columns:repeat(${perRow}, minmax(0, 1fr))`
        "
      >
        <div
          v-for="(n, index) in isSportsTabActive ? 6 : 12"
          :key="`skeleton-${index}`"
          :class="[
            'skeleton-loading',
            isSportsTabActive ? 'sport-layout' : 'casino-layout',
          ]"
        ></div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.searchResults {
  @apply w-[100%] max-h-[56vh] md:max-h-[550px] rounded bg-[#030a119c] mt-1 flex flex-col absolute z-[90];

  .skeleton-rows {
    @apply w-full grid overflow-hidden;
    &.casino-layout {
      min-height: 100% !important;
    }
    &.sport-layout {
      @apply gap-2 md:gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3;
    }
    .skeleton-loading {
      @apply w-full bg-bg-primary/20;
      &.casino-layout {
        @apply h-auto aspect-[25/29];
      }
      &.sport-layout {
        @apply h-14;
      }
    }
  }

  .filterWrapper {
    @apply hidden border-b border-solid border-white/10 p-2;

    .btn {
      @apply bg-texts/10 rounded no-underline text-texts/80 inline-flex m-1 py-1 px-2;

      &.categoryActive {
        @apply bg-primary text-black font-semibold;
      }
      span {
        @apply flex items-center cursor-pointer text-xs;
      }
    }

    &.sleletonSports {
      @apply flex items-center;

      .btn {
        @apply h-6 w-20 bg-bg-primary/20;
      }
    }
  }

  .resultsWrapper {
    @apply px-3 mt-3 flex flex-wrap overflow-auto;
    &::-webkit-scrollbar {
      @apply hidden invisible;
    }

    .sportsItems {
      @apply grid w-full gap-2 md:gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 mb-4;
    }
  }
}
:deep(.games-row) {
  @apply mt-0;
}
</style>
